import {
  HexSLTypes,
  NumericMeasureAggTypes,
  guardNever,
  titleCase,
} from "../index.js";

/**
 * Note: In the public HexSL API, "id" maps to the internal `name` field for a given
 * semantic entity, and "name" maps to the internal `title` field (the human-friendly
 * display name for a given semantic entity). `title` is always set by the Hex (public)
 * importer, but nullable because third-party importers may omit it. These helpers resolve
 * the user-facing display string by preferring `title` and falling back to `name`.
 */

export function semanticDatasetTitleString(dataset: {
  name: string;
  title?: string | null;
}): string {
  return dataset.title || dataset.name;
}

export function semanticViewTitleString(view: {
  name: string;
  title?: string | null;
}): string {
  return view.title || view.name;
}

export function semanticMeasureTitleString(measure: {
  name: string;
  title?: string | null;
}): string {
  return measure.title || measure.name;
}

export function semanticDimensionTitleString(dimension: {
  name: string;
  title?: string | null;
}): string {
  return dimension.title || dimension.name;
}

export function semanticMeasureDisplayExpression(
  measure: HexSLTypes.Measure,
): string {
  const sql = measure.sql ? replaceDatasetReferences(measure.sql) : undefined;
  const expression: string =
    sql ?? (typeof measure.calc === "string" ? measure.calc : "");
  if (!expression) {
    return "";
  }

  // This is not necessarily a valid SQl/calc expression, but we want to include
  // the aggregation type when displaying the sql expression.
  if (NumericMeasureAggTypes.guard(measure.type)) {
    return `${titleCase(measure.type)}(${expression})`;
  }

  return expression;
}

export function semanticDimensionDisplayExpression(
  dimension: HexSLTypes.Dimension,
): {
  /** May be an empty string */
  expression: string;
  label: string;
} {
  if (dimension.sql != null || dimension.calc != null) {
    const sql = dimension.sql
      ? replaceDatasetReferences(dimension.sql)
      : undefined;
    const expression: string =
      sql ?? (typeof dimension.calc === "string" ? dimension.calc : "");

    return {
      expression,
      label: "Expression",
    };
  }

  return {
    expression: dimension.column || dimension.name,
    label: "Column",
  };
}

export function semanticMeasureFilterDisplayExpression(
  filter: HexSLTypes.MeasureFilter,
): string | null {
  if (filter.calc != null) {
    if (typeof filter.calc === "string") {
      return filter.calc;
    } else {
      // This is a terrible way to display things but should work for now
      return JSON.stringify(filter.calc, null, 2);
    }
  }

  if (filter.sql) {
    return replaceDatasetReferences(filter.sql);
  }

  return null;
}

function replaceDatasetReferences(sql: string): string {
  return (
    sql
      // "${DATASET}.foo" -> "foo"
      .replaceAll("${DATASET}.", "")
      // "${DATASET.foo}" -> "foo"
      .replaceAll(/\${DATASET.([\w_]*)}/g, "$1")
      // "${bar.foo}" -> "bar.foo"
      // or "${bar}.foo" -> "bar.foo"
      .replaceAll(/\${([\w_.]*)}/g, "$1")
  );
}

export function semanticMeasureAggType(measure: HexSLTypes.Measure): string {
  if (NumericMeasureAggTypes.guard(measure.type)) {
    return measure.type;
  }

  return `CUSTOM`;
}

export function compactSemanticMeasureAggType(
  measure: HexSLTypes.Measure,
): string {
  if (NumericMeasureAggTypes.guard(measure.type)) {
    switch (measure.type) {
      case "sum":
        return "SUM";
      case "avg":
        return "AVG";
      case "min":
        return "MIN";
      case "max":
        return "MAX";
      case "count":
        return "CNT";
      case "countdistinct":
        return "CND";
      case "stddev":
        return "STD";
      case "stddevpop":
        return "STP";
      case "sumboolean":
        return "SBT";
      default:
        return "UNKNOWN";
    }
  }
  return `CSM`;
}

export function semanticJoinRelationshipString(
  relationship: HexSLTypes.JoinRelationship,
): string {
  switch (relationship) {
    case "one_to_one":
      return "One-to-one";
    case "many_to_one":
      return "Many-to-one";
    case "one_to_many":
      return "One-to-many";
    case "many_to_many":
      return "Many-to-many";
    default:
      guardNever(relationship, "relationship");
      return "Unknown Relationship";
  }
}

export function semanticJoinRelationshipDescription(
  relationship: HexSLTypes.JoinRelationship,
  baseTitle: string,
  targetTitle: string,
): string {
  switch (relationship) {
    case "one_to_one":
      return `Each row from ${baseTitle} can only match to a single row from ${targetTitle}, and vice versa.`;
    case "one_to_many":
      return `Each row from ${baseTitle} can match to many rows from ${targetTitle}.`;
    case "many_to_one":
      return `Many rows from ${baseTitle} can match to a single row from ${targetTitle}.`;
    case "many_to_many":
      return `Each row from ${baseTitle} can match to many rows from ${targetTitle}, and vice versa.`;
    default:
      guardNever(relationship, "relationship");
      return "Unknown Relationship";
  }
}
